import { create } from "zustand";
import { getWebSocketUrl } from "../config";
import { apiClient } from "../services/apiClient";
import { authTokenService } from "../services/authTokenService";
import { UserModel as User } from "../shared/util/src/types";

interface UserState {
  currentUser: User | null;
  token: string | null;
  users: User[];

  setUserToken: (token: string | null) => void;
  setCurrentUser: (user: User) => void;
  getCurrentUser: () => User | null;
  getUserToken: () => string | null;
  getUsers: () => User[];
  signIn: (login: string, password: string) => Promise<void>;
  fetchUser: (token?: string) => Promise<void>;
  fetchUsers: (token?: string) => Promise<void>;
  signOut: () => void;
}

const serverUrl = getWebSocketUrl();

export const useUserStore = create<UserState>((set, get) => ({
  currentUser: null,
  token: authTokenService.getAccessToken(),
  users: [],

  setCurrentUser: (currentUser) => {
    set({ currentUser });
  },

  getCurrentUser: () => get().currentUser,

  getUsers: () => {
    return get().users;
  },

  setUserToken: (token) => {
    if (token) {
      authTokenService.setAccessToken(token);
    } else {
      authTokenService.clearTokens();
    }
    set({ token });
  },

  getUserToken: () => {
    const token = authTokenService.getAccessToken();
    if (token !== get().token) {
      set({ token });
    }
    return token;
  },

  signIn: async (login, password) => {
    const response = await fetch(`${serverUrl}/auth/sign-in`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Client-Type": "WEB",
      },
      body: JSON.stringify({ login, password }),
      credentials: "include",
    });

    if (response.ok) {
      const data = (await response.json()) as {
        access_token: string;
        refresh_token?: string;
      };
      authTokenService.setTokens(data.access_token, data.refresh_token);
      set({ token: data.access_token });
    } else {
      authTokenService.clearTokens();
      set({ token: null, currentUser: null });
      let message: string | undefined;
      try {
        const body = await response.json();
        if (typeof body?.message === "string") {
          message = body.message;
        }
      } catch {
        // ignore JSON parse failure
      }
      throw new Error(message ?? "Identifiants invalides");
    }
  },

  fetchUser: async (token) => {
    if (token && token !== authTokenService.getAccessToken()) {
      authTokenService.setAccessToken(token);
    }

    try {
      const user = await apiClient.get<User>("/users/me");
      set({ currentUser: user });
    } catch {
      authTokenService.clearTokens();
      set({ currentUser: null, token: null });
    }
  },

  fetchUsers: async (token) => {
    if (token && token !== authTokenService.getAccessToken()) {
      authTokenService.setAccessToken(token);
    }

    try {
      const users = await apiClient.get<User[]>("/users");
      set({ users });
    } catch {
      set({ users: [] });
    }
  },

  signOut: () => {
    const accessToken = authTokenService.getAccessToken();
    if (accessToken) {
      void fetch(`${serverUrl}/auth/logout`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          "X-Client-Type": "WEB",
        },
      }).catch(() => undefined);
    }
    authTokenService.clearTokens();
    set({ currentUser: null, token: null, users: [] });
  },
}));

authTokenService.subscribe((accessToken) => {
  useUserStore.setState({ token: accessToken });
});

authTokenService.onAuthFailure(() => {
  useUserStore.getState().signOut();
});

authTokenService.startProactiveRefresh(serverUrl);
